import { useEffect } from "react";
import { useProductData } from "@/zustand/productStore";
import { useCartData } from "@/zustand/cartStore";
import { useMarketingStore } from "@/zustand/emailMarketingStore";
import getDefaultVariantsState from "@/func/defaultVariants";
import { buildSelectedProduct } from "@/func/buildSelectedProduct";
import { readPreselection, resolvePreselection } from "@/func/preselectedProduct";

// Selects the product for a checkout without the product picker, from the
// product page selection in the URL (see PRESELECTION_PARAMS).
export const usePreselectedProduct = (enabled) => {
  const productData = useProductData();
  const { selectedProduct, setSelectedProduct, isSubscribe, setIsSubscribe, setVariants, setWarrantyState } =
    useCartData();

  // The layout refetches the pack when isMobile flips (768–1070px), and every fetch
  // re-merges subscription prices when the pack enables subscription. Keep the store
  // on the purchase type already chosen, so the summary still finds the product.
  useEffect(() => {
    if (!enabled || !selectedProduct || !productData?.original_products) return;
    productData.setProductsBySubscription(isSubscribe);
  }, [enabled, productData?.original_products]);

  // Email-marketing free shipping: the picker zeroes shipping in ProductsList, which
  // this design doesn't render. Re-applied after a refetch restores pack prices.
  const { freeShippingApplied } = useMarketingStore();
  useEffect(() => {
    if (!enabled || !freeShippingApplied || !productData?.products) return;
    if (productData.products.some((p) => Number(p.shipping_price) !== 0)) {
      productData.changeProductsShippingPrice(0);
    }
    if (selectedProduct && Number(selectedProduct.shipping_price) !== 0) {
      setSelectedProduct({ ...selectedProduct, shipping_price: 0 });
    }
  }, [enabled, freeShippingApplied, productData?.products, selectedProduct]);

  useEffect(() => {
    if (!enabled || selectedProduct || !productData?.products) return;

    const originalProducts = productData.original_products?.length
      ? productData.original_products
      : productData.products;
    const selection = resolvePreselection(
      originalProducts,
      productData.subscription,
      readPreselection(window.location.search)
    );
    if (!selection) return;

    setIsSubscribe(selection.isSubscribe);
    productData.setProductsBySubscription(selection.isSubscribe);

    // the store updates synchronously, so this reads the prices for the chosen purchase type
    const products = useProductData.getState().products || productData.products;
    const product = products.find((p) => p.amount === selection.amount);
    if (!product) return;

    const enableVariants = productData.enable_color_variants || productData.enable_size_variants;
    if (enableVariants) {
      setVariants(getDefaultVariantsState(productData));
    }
    if (productData.preselect_warranty && productData.add_warranty_for_product) {
      setWarrantyState(true);
    }

    setSelectedProduct(
      buildSelectedProduct(product, {
        isSubscribe: selection.isSubscribe,
        subscriptionProducts: productData.subscription?.products,
        shouldUseBundleTitle: productData.shouldUseBundleTitle,
        thankYouPageProductName: window?.subscription_template?.tr_thankyou_page_product_name,
      })
    );
  }, [enabled, productData, selectedProduct, setSelectedProduct, setIsSubscribe, setVariants, setWarrantyState]);
};
